import { useState, useEffect, useCallback } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';

export interface Nationalities {
  id: number;
  name: string;
}

interface UseNationalitiesReturn {
  nationalities: Nationalities[];
  nationalitiesOptions: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useNationalities = (): UseNationalitiesReturn => {
  const [nationalities, setNationalities] = useState<Nationalities[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNationalities = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<{
        success: boolean;
        nationalities: Nationalities[];
      }>(
        '/api/nationalities',
        {},
        30 * 60 * 1000 // 30 minutes TTL - nationalities rarely change
      );

      if (response.success && response.nationalities) {
        // sort A → Z by name
        const sortedNationalities = response.nationalities.sort(
          (a: Nationalities, b: Nationalities) => a.name.localeCompare(b.name)
        );

        setNationalities(sortedNationalities);
        console.log('Fetched nationalities (cached/sorted):', sortedNationalities);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching nationalities:', err);
      setError('Failed to fetch nationalities');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed since api and setters are stable

  useEffect(() => {
    fetchNationalities();
  }, [fetchNationalities]); // ✅ Now includes the dependency

  // Convert nationalities options format for dropdowns (already sorted, remove duplicates)
  const nationalitiesOptions = [...new Set(nationalities.map(nationality => nationality.name))];

  return {
    nationalities,
    nationalitiesOptions,
    loading,
    error,
    refetch: fetchNationalities
  };
};